import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';

import Confirm from 'components/Confirm';
import Dot from 'components/Dot';
import Message from 'components/Message';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';

import { ITEM_AVAILABILITY_STATUS, ITEM_AVAILABILITY_STATUS_MAPPING } from '../Items/SkuItems.constants';
import { skuItemAvailabilityStatusMessages } from '../Items/SkuItems.messages';
import messages from './SkuAvailabilityModal.messages';

import './SkuAvailabilityModal.scss';

export const SKU_AVAILABILITY_MODAL_NAME = 'SkuAvailabilityModal';

// The cancel endpoint flips the item sold out for the day, so "Available" is not a choice here.
export const OUT_OF_STOCK_STATUS_OPTIONS = [
  ITEM_AVAILABILITY_STATUS.UNAVAILABLE_TODAY,
  ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY,
];

const OPTION_DESCRIPTION_MESSAGES = {
  [ITEM_AVAILABILITY_STATUS.UNAVAILABLE_TODAY]: messages.unavailableTodayDescription,
  [ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY]: messages.unavailableIndefinitelyDescription,
};

const { block, element } = bem('sku-availability-modal');

const StatusMarker = ({ status }) => {
  if (status === ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY) {
    return <Isvg className={element('status-marker', { icon: true })} src={ICONS.noSymbol} />;
  }
  return <Dot className={element('status-marker')} color={ITEM_AVAILABILITY_STATUS_MAPPING[status]?.color} largeSize />;
};

StatusMarker.propTypes = {
  status: PropTypes.oneOf(OUT_OF_STOCK_STATUS_OPTIONS).isRequired,
};

const SkuAvailabilityModal = ({
  itemName,
  isOnlyItemInOrder = false,
  onConfirm,
  onCancel,
  initialStatus = ITEM_AVAILABILITY_STATUS.UNAVAILABLE_TODAY,
}) => {
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);

  const titleMessage = isOnlyItemInOrder ? messages.titleSingleItem : messages.titleMultiItem;
  const introMessage = isOnlyItemInOrder ? messages.introSingleItem : messages.introMultiItem;

  return (
    <Confirm
      className={block()}
      title={<Message message={titleMessage} values={{ itemName }} />}
      hasCloseButton
      onCancel={onCancel}
      cancelNode={<Message message={messages.cancel} />}
      acceptNode={<Message message={messages.continue} />}
      onConfirm={() => onConfirm(selectedStatus)}
      size="complex">
      {({ title, buttons }) => (
        <>
          {title}
          <p className={element('intro')}>
            <Message message={introMessage} />
          </p>
          <div className={element('options')}>
            {OUT_OF_STOCK_STATUS_OPTIONS.map((status, idx) => {
              const isSelected = selectedStatus === status;
              const statusMessage =
                skuItemAvailabilityStatusMessages[ITEM_AVAILABILITY_STATUS_MAPPING[status]?.message];

              return (
                <React.Fragment key={status}>
                  {idx > 0 && <div className={element('divider')} />}
                  <label className={element('option', { selected: isSelected })}>
                    <div className={element('option-left')}>
                      <StatusMarker status={status} />
                      <span className={element('option-label')}>
                        <Message message={statusMessage} />
                      </span>
                    </div>
                    <input
                      type="radio"
                      className={element('radio')}
                      name="sku-availability-status"
                      value={status}
                      checked={isSelected}
                      onChange={() => setSelectedStatus(status)}
                    />
                  </label>
                  <p className={element('option-description')}>
                    <Message message={OPTION_DESCRIPTION_MESSAGES[status]} />
                  </p>
                </React.Fragment>
              );
            })}
          </div>
          {buttons}
        </>
      )}
    </Confirm>
  );
};

SkuAvailabilityModal.propTypes = {
  itemName: PropTypes.string,
  isOnlyItemInOrder: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  initialStatus: PropTypes.oneOf(OUT_OF_STOCK_STATUS_OPTIONS),
};

export default SkuAvailabilityModal;
